import {
  HttpClient,
  HttpContext,
  HttpErrorResponse,
  HttpParams,
} from '@angular/common/http'
import { Injectable } from '@angular/core'
import { ERROR_INTERCEPTOR_BYPASS, HANDLE_ERROR_AND_THROW } from '@helios/utils'
import { Observable, of, throwError } from 'rxjs'
import { catchError, map, shareReplay } from 'rxjs/operators'
import { RpsSource, SourceFieldCatalog } from '../models/field-catalog.model'
import { FieldCatalogService } from './field-catalog.service'

/**
 * The per-source field-catalog endpoints (PVAL-26129 §2, backend PVAL-26445 / PVAL-26465).
 *
 * These REPLACE the shared `helios-flex-transactions/transactions/fields?accountId=&sources=` route,
 * which is deprecated and whose `isDynamic` cannot be used for routing: only its Enfusion columns
 * are marked, and its whole Prometheus half — Aggregate Info included — always reports
 * `isDynamic: false`. Each of these returns a bare {@link SourceFieldCatalog}, NOT wrapped in
 * `sources[]`.
 *
 * Confirmed against beta: with `<base href="/helios/">` these resolve under
 * `/helios/helios-flex-transactions/`. The flex-transactions service lives on its own host rather
 * than behind the helios-ui origin, so for local beta dev a `/helios/helios-flex-transactions/**`
 * proxy entry (rewriting the prefix onto
 * `https://helios-flex-transactions.uswe2.beta.aws.cwan.io/api`) must be added ahead of the generic
 * `/helios/helios-*` catch-all — otherwise the catch-all sends it to helios-ui and 404s.
 *
 * That proxy entry is a LOCAL-DEV-ONLY edit and is deliberately NOT committed: CLAUDE.md requires
 * `proxy.conf.beta.json` to be reverted before committing. In deployed environments the gateway
 * routes this prefix; the proxy file only matters for `yarn start` against beta. The CIAM bearer
 * token is attached globally by AuthTokenInterceptor.
 */
export const ENFUSION_FIELD_CATALOG_URL = `helios-flex-transactions/flex-transactions/fields`

/**
 * Prometheus' catalog. Takes NO parameters and is neither account- nor session-scoped: it is one
 * global set of observed `agg_info` keys, and a group only appears once a key has been seen.
 */
export const PROMETHEUS_FIELD_CATALOG_URL = `helios-flex-transactions/flex-external-transactions/fields`

/** An empty catalog, returned when the backend has no metadata for a source — legitimate, not an
 * error (CW has no endpoint at all; Enfusion 404s when RPS holds no metadata for the client). */
const emptyCatalog = (source: RpsSource): SourceFieldCatalog => ({
  source,
  groups: [],
  columns: [],
})

/**
 * The cache key for a source.
 *
 * Enfusion is per-account: its catalog is this CW client's `tran_data` metadata, resolved per
 * account. Prometheus is NOT — it is the global observed-key set, so caching it per account would
 * issue one identical request per account visited. It therefore lives under an account-agnostic key,
 * which also means {@link HttpFieldCatalogService.invalidate} (keyed on account) correctly leaves it
 * alone: nothing about changing account can change it.
 */
/**
 * How a catalog GET asks the app-wide ErrorInterceptor to hand failures BACK instead of swallowing
 * them into `EMPTY` — which would complete the catalog with nothing, leave the columns spinner up
 * forever, and skip both the 404 → empty-catalog rule and the component's Retry strip.
 *
 *  - `404` bypasses the interceptor entirely: it is "RPS has no metadata for this client", a
 *    legitimate empty catalog, not an error worth logging.
 *  - every other failure is still logged by the interceptor and then rethrown to `getCatalog`.
 */
const catalogRequestContext = (): HttpContext =>
  new HttpContext()
    .set(ERROR_INTERCEPTOR_BYPASS, [404])
    .set(HANDLE_ERROR_AND_THROW, true)

const cacheKeyFor = (accountId: number, source: RpsSource): string =>
  source === 'PROMETHEUS' ? `global:PROMETHEUS` : `${accountId}:${source}`

@Injectable({
  providedIn: 'root',
})
export class HttpFieldCatalogService extends FieldCatalogService {
  private readonly cache = new Map<string, Observable<SourceFieldCatalog>>()

  constructor(private readonly http: HttpClient) {
    super()
  }

  getCatalog(
    accountId: number,
    source: RpsSource,
  ): Observable<SourceFieldCatalog> {
    const key = cacheKeyFor(accountId, source)
    const cached = this.cache.get(key)
    if (cached) return cached

    const request = this.fetch(accountId, source).pipe(
      // A 404 is the backend saying "there is no metadata for this client", not a failure: RPS holds
      // no Enfusion metadata for some CW clients. It resolves to an empty catalog so the panel shows
      // its own empty state (and the sibling grid still renders) instead of an error strip offering a
      // Retry that can only 404 again. Every OTHER status still propagates, so the component keeps
      // classifying transient failures as retryable.
      catchError((err: unknown) => {
        if (err instanceof HttpErrorResponse && err.status === 404) {
          return of(emptyCatalog(source))
        }
        // `throwError(err)`, NOT `throwError(() => err)`. This repo is on RxJS 6, where `throwError`
        // takes the error VALUE — the factory overload only arrives in RxJS 7. Passing a thunk here
        // emits the FUNCTION as the error, so every downstream `err instanceof HttpErrorResponse`
        // check fails and a 404 is indistinguishable from a 500.
        return throwError(err)
      }),
      // On a terminal error, evict this key so the cache never retains a permanently-errored
      // observable. Without this, shareReplay would replay the same failure to every future caller
      // and the Retry affordance (which re-calls getCatalog) would get the cached failing observable
      // back — never issuing a fresh HTTP request.
      catchError((err: unknown) => {
        if (this.cache.get(key) === request) this.cache.delete(key)
        return throwError(err)
      }),
      // Replay the resolved catalog to every grid rebuild without a refetch, and de-dupe concurrent
      // in-flight requests for the same key.
      shareReplay({ bufferSize: 1, refCount: false }),
    )
    this.cache.set(key, request)
    return request
  }

  /**
   * One GET per source. The response body IS the source's catalog — there is no `sources[]` wrapper
   * to unpick any more, so a `find` that used to silently yield the empty catalog when the backend
   * answered for a different source cannot happen.
   *
   * `source` is stamped from the request rather than trusted from the body: the Prometheus endpoint
   * is shared across accounts and the Enfusion one is keyed only by accountId, so the caller's own
   * source is the authoritative answer to "whose catalog is this".
   */
  private fetch(
    accountId: number,
    source: RpsSource,
  ): Observable<SourceFieldCatalog> {
    if (source === 'PROMETHEUS') {
      return this.http
        .get<SourceFieldCatalog>(PROMETHEUS_FIELD_CATALOG_URL, {
          context: catalogRequestContext(),
        })
        .pipe(map(response => this.withSource(response, source)))
    }
    if (source === 'ENFUSION') {
      // 400s without an accountId, so the caller must have focused an account first — the component
      // skips the fetch entirely until it has.
      return this.http
        .get<SourceFieldCatalog>(ENFUSION_FIELD_CATALOG_URL, {
          params: new HttpParams().set('accountId', String(accountId)),
          context: catalogRequestContext(),
        })
        .pipe(map(response => this.withSource(response, source)))
    }
    // CW is not catalogued by the backend and has no endpoint; asking would 404 on the gateway.
    return of(emptyCatalog(source))
  }

  /** Normalise a response body into a complete catalog, tolerating a null/partial payload. */
  private withSource(
    response: SourceFieldCatalog | null,
    source: RpsSource,
  ): SourceFieldCatalog {
    if (!response) return emptyCatalog(source)
    return {
      source,
      groups: response.groups ?? [],
      columns: response.columns ?? [],
    }
  }

  invalidate(accountId: number): void {
    for (const key of [...this.cache.keys()]) {
      if (key.startsWith(`${accountId}:`)) this.cache.delete(key)
    }
  }
}
